import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../common/Button';
import TradeTicketVisual from './TradeTicketVisual';

const ease = [0.16, 1, 0.3, 1];

const HeroSection = () => {
  return (
    <section className="relative pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
      {/* background layers */}
      <div className="absolute inset-0 bg-radial-amber pointer-events-none" />
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="noise" />

      <div className="container-prose relative">
        {/* eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="flex justify-center mb-7"
        >
          <Link
            to="/product"
            className="group inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-white/[0.04] ring-1 ring-white/10 hover:ring-white/25 transition-colors"
          >
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.1em] uppercase text-amber-light">
              <span className="w-1.5 h-1.5 rounded-full bg-amber animate-pulse-soft" />
              Pre-launch
            </span>
            <span className="w-px h-3 bg-white/15" />
            <span className="text-[12.5px] text-ink-faint group-hover:text-canvas-white transition-colors">
              Booking demos with Gulf trading desks
            </span>
            <ArrowRight size={12} className="text-ink-faint group-hover:text-canvas-white group-hover:translate-x-0.5 transition-all" />
          </Link>
        </motion.div>

        {/* headline */}
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.05, ease }}
          className="text-hero font-bold text-canvas-white text-center text-balance max-w-5xl mx-auto"
        >
          Trade booking,
          <br className="hidden sm:block" />{' '}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-amber via-amber-light to-amber bg-clip-text text-transparent">
              done in seconds.
            </span>
          </span>
        </motion.h1>

        {/* subhead */}
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.18, ease }}
          className="mt-7 text-[17px] md:text-[19px] leading-relaxed text-ink-faint text-center max-w-2xl mx-auto text-balance"
        >
          Core Trade reads your trade confirmations the way a junior trader would —
          in seconds, with every field structured, audited, and ready to post to your TRM.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.3, ease }}
          className="mt-10 flex items-center justify-center gap-3"
        >
          <Button to="/request-demo" variant="primary" size="lg" withArrow>
            Book a demo
          </Button>
          <Button to="/product" variant="outline" size="lg">
            See how it works
          </Button>
        </motion.div>

        {/* meta strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11.5px] text-ink-faint"
        >
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-success" />
            SOC 2 on roadmap
          </span>
          <span className="hidden sm:inline-block w-px h-3 bg-white/10" />
          <span>GCC data residency</span>
          <span className="hidden sm:inline-block w-px h-3 bg-white/10" />
          <span>Built for Allegro · Endur · RightAngle</span>
        </motion.div>

        {/* product visual */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.55, ease }}
          className="mt-20"
        >
          <TradeTicketVisual />
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
